import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CalendarPlus, CheckCircle, XCircle, Clock, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkerRequests = () => {
  const [selectedDates, setSelectedDates] = useState([]);
  const [note, setNote] = useState('');
  const [requests, setRequests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dateInput, setDateInput] = useState('');

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/worker/attendance-requests`, { withCredentials: true });
      setRequests(res.data);
    } catch (e) { /* silenced */ }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const addDate = () => {
    if (dateInput && !selectedDates.includes(dateInput)) {
      setSelectedDates(prev => [...prev, dateInput].sort());
      setDateInput('');
    }
  };

  const removeDate = (d) => setSelectedDates(prev => prev.filter(x => x !== d));

  const handleSubmit = async () => {
    if (selectedDates.length === 0) return alert('Seleziona almeno una data');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/worker/attendance-requests`, {
        dates: selectedDates, note
      }, { withCredentials: true });
      setSelectedDates([]);
      setNote('');
      fetchRequests();
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore invio richiesta');
    } finally { setSubmitting(false); }
  };

  const statusIcon = (status) => {
    if (status === 'approved') return <CheckCircle className="w-4 h-4 text-[#34C759]" />;
    if (status === 'rejected') return <XCircle className="w-4 h-4 text-[#FF3B30]" />;
    return <Clock className="w-4 h-4 text-[#FF9500]" />;
  };

  const statusLabel = (status) => {
    if (status === 'approved') return 'Approvata';
    if (status === 'rejected') return 'Rifiutata';
    return 'In attesa';
  };

  const formatDate = (d) => {
    const parts = d.split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  return (
    <Card className="border-[#E4E4E7] shadow-sm" data-testid="worker-requests-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold text-[#09090B] flex items-center gap-2">
          <CalendarPlus className="w-5 h-5 text-[#0055FF]" />
          Richiedi Giorni Mancanti
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Date selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-[#09090B]">Seleziona date</label>
          <div className="flex gap-2">
            <input
              type="date"
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
              data-testid="request-date-input"
              className="flex-1 px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
            />
            <Button
              size="sm"
              onClick={addDate}
              disabled={!dateInput}
              data-testid="request-add-date-btn"
              className="bg-[#0055FF] hover:bg-[#0040D0]"
            >
              Aggiungi
            </Button>
          </div>
          {selectedDates.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedDates.map(d => (
                <span key={d} className="inline-flex items-center gap-1 px-2 py-1 bg-[#0055FF]/10 text-[#0055FF] rounded-md text-xs font-medium">
                  {formatDate(d)}
                  <button onClick={() => removeDate(d)} className="hover:text-[#FF3B30]">&times;</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Note */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-[#09090B]">Nota (opzionale)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Motivazione della richiesta..."
            data-testid="request-note-input"
            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm resize-none h-16 focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
          />
        </div>

        <Button
          onClick={handleSubmit}
          disabled={submitting || selectedDates.length === 0}
          data-testid="request-submit-btn"
          className="w-full bg-[#0055FF] hover:bg-[#0040D0]"
        >
          <Send className="w-4 h-4 mr-2" />
          {submitting ? 'Invio...' : `Invia Richiesta (${selectedDates.length} giorni)`}
        </Button>

        {/* Request history */}
        {requests.length > 0 && (
          <div className="pt-3 border-t border-[#E4E4E7]">
            <h4 className="text-sm font-semibold text-[#09090B] mb-2">Le mie richieste</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {requests.map(r => (
                <div key={r.request_id} className="flex items-start justify-between p-2 bg-[#F4F4F5] rounded-md" data-testid={`request-item-${r.request_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {statusIcon(r.status)}
                      <span className="text-xs font-semibold">{statusLabel(r.status)}</span>
                    </div>
                    <p className="text-xs text-[#71717A] mt-0.5">
                      {r.dates.map(formatDate).join(', ')}
                    </p>
                    {r.note && <p className="text-xs text-[#71717A] italic mt-0.5">{r.note}</p>}
                    {r.reject_reason && <p className="text-xs text-[#FF3B30] mt-0.5">Motivo: {r.reject_reason}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkerRequests;
